import { useState } from 'react'

import type { Subject } from '../../api/subjects'
import type { Appointment, WantedAppointment } from '../../api/timetable'
import { Button } from '../../ui/Control'
import { SubjectPicker } from '../../ui/SubjectPicker'
import type { Repetition } from './RepeatFields'
import { Repeats } from './Repeats'
import { Session } from './Session'
import { localValue, momentOfLocal, readableTime } from './week'
import { RichText } from '../../ui/RichText'
import { useSaving } from '../../ui/useSaving'

/**
 * One appointment, opened — **two tabs**, because two different jobs meet here.
 *
 * *Appointment* is what is on: when, where, with whom, about what. *Session* is what came of it — the
 * write-up, the material that belongs to it, and the open points and cards it produced. They are tabs
 * and not one long surface because they are wanted at different moments: the first before a lecture
 * and the second after it, and everything on one page would mean scrolling past a room field to
 * write two sentences on the sofa.
 *
 * The appointment stands first and is what opens. Somebody who taps a lecture in the week grid is
 * more often asking when and where it is than sitting down to write about it, and the second tab is
 * one tap away.
 *
 * **Which half is open lives in the address**, so the back button walks between them and a link
 * carries it — which is what makes a search hit on somebody's own write-up land on that write-up
 * rather than on a room number.
 *
 * Three further things about this surface are decisions rather than layout.
 *
 * **The subject is a picker with one slot.** An appointment is about one thing — the single exception to
 * the many-valued belonging everything else has — so choosing another replaces the one that is set.
 * Where no subject is set and the source has a code of its own, that code is shown as text with the
 * offer to make a subject of it: the plan is readable the minute it arrives, before anybody has
 * decided anything.
 *
 * **A hand correction says so, and can be taken back.** Every field somebody changes here is held
 * against every future reading of the source. That is a promise, so it is visible where it applies
 * and there is a control beside it that gives the field back to the source. Without that, one hasty
 * correction would freeze a room for ever.
 *
 * **A change to an appointment in a series asks which it means.** Only this one, or all of them — and
 * "all of them" deliberately does not move the times, because each appointment has a moment of its own.
 */
export interface AppointmentSheetProps {
  readonly appointment: Appointment
  readonly subjects: readonly Subject[]
  readonly onChange: (wanted: WantedAppointment, scope: 'one' | 'series') => void
  readonly onRelease: (fact: string) => void
  readonly onCreateSubject: (name: string) => void
  readonly onRepeat: (repetition: Repetition) => void
  readonly onStopRepeating: () => void
  readonly onRemove: () => void
  /** Which half is open. It lives in the address, so a link carries it and the back button walks it. */
  readonly tab: 'appointment' | 'session'
  readonly onTab: (tab: 'appointment' | 'session') => void
  /** Told when something was written or linked, so the plan can redraw the day. */
  readonly onSessionChanged: () => void
  readonly onClose: () => void
}

export function AppointmentSheet({
  appointment,
  subjects,
  onChange,
  onRelease,
  onCreateSubject,
  onRepeat,
  onStopRepeating,
  onRemove,
  tab,
  onTab,
  onSessionChanged,
  onClose,
}: AppointmentSheetProps) {
  /*
   * Seeded once and never synchronised afterwards: the screen keys this surface by what the service
   * last said about the appointment, so a change arriving is a new surface with the new values in it.
   */
  const [title, setTitle] = useState(appointment.title)
  const [room, setRoom] = useState(appointment.room)
  const [teacher, setTeacher] = useState(appointment.teacher)
  const [note, setNote] = useState(appointment.note)
  const [scope, setScope] = useState<'one' | 'series'>('one')

  /*
   * Holding only means something where there is a source to hold *against*.
   *
   * On an appointment somebody typed, every value is already theirs — saying "your own time" beside
   * a control that hands it back to nobody was words about an internal flag rather than about
   * anything on the screen.
   */
  const fromASource = appointment.origin.kind !== 'by-hand' && appointment.origin.source !== ''
  const held = (fact: string) => fromASource && appointment.held.includes(fact)
  const chosen =
    appointment.subject === null
      ? []
      : [{ id: appointment.subject.id, name: appointment.subject.name, inherited: false }]

  const save = (wanted: WantedAppointment) => {
    onChange(wanted, scope)
  }

  // Saved as it is written, like every other text in MyLib.
  const savingNote = useSaving((wanted) => {
    save({ note: wanted })
    return Promise.resolve()
  })

  return (
    <article
      className="sheet sheet--appointment"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          onClose()
        }
      }}
    >
      <header className="sheet__head">
        <Button
          tone="bare"
          className="sheet__close"
          icon="close"
          iconOnlyLabel="Close this appointment"
          onClick={onClose}
        />
        <h2 className="sheet__title-text">{appointment.title}</h2>
        <p className="sheet__facts">
          <span>
            {readableTime(new Date(appointment.startsAt), appointment.zone)}–
            {readableTime(new Date(appointment.endsAt), appointment.zone)}
          </span>
          <span aria-hidden="true">·</span>
          <span>{appointment.zone}</span>
          {appointment.cancelled && (
            <>
              <span aria-hidden="true">·</span>
              <span className="appointment-detail__warn">Cancelled</span>
            </>
          )}
          {appointment.goneFromSourceAt !== null && (
            <>
              <span aria-hidden="true">·</span>
              <span className="appointment-detail__warn">
                No longer offered by {appointment.origin.source}
              </span>
            </>
          )}
        </p>
      </header>

      <div className="sheet__tabs" role="tablist" aria-label="This appointment">
        <button
          type="button"
          className="sheet__tab"
          role="tab"
          id="appointment-tab-appointment"
          aria-selected={tab === 'appointment'}
          aria-controls="appointment-panel-appointment"
          onClick={() => {
            onTab('appointment')
          }}
        >
          Appointment
        </button>
        <button
          type="button"
          className="sheet__tab"
          role="tab"
          id="appointment-tab-session"
          aria-selected={tab === 'session'}
          aria-controls="appointment-panel-session"
          onClick={() => {
            onTab('session')
          }}
        >
          Session
          {/* What is already there, said on the tab, so the second half is not a place to go and
              look. A session nobody wrote about says nothing at all. */}
          {appointment.writeupLine !== null && (
            <span className="sheet__tab-mark" aria-hidden="true" />
          )}
        </button>
      </div>

      {tab === 'session' && (
        <div
          /*
            The session is the one tab that is a writing surface rather than a form, and it says so
            here — see `appointment-detail--session` in the stylesheet for what it buys.
          */
          className="appointment-detail appointment-detail--session"
          role="tabpanel"
          id="appointment-panel-session"
          aria-labelledby="appointment-tab-session"
        >
          <Session appointment={appointment} onChanged={onSessionChanged} />
        </div>
      )}

      <div
        className="appointment-detail"
        role="tabpanel"
        id="appointment-panel-appointment"
        aria-labelledby="appointment-tab-appointment"
        hidden={tab !== 'appointment'}
      >
        {appointment.seriesId !== null && (
          <section className="appointment-detail__part">
            <h3 className="appointment-detail__part-title">One of a run</h3>
            <div
              className="appointment-detail__scope"
              role="group"
              aria-label="A change applies to"
            >
              <button
                type="button"
                className="chip"
                aria-pressed={scope === 'one'}
                onClick={() => {
                  setScope('one')
                }}
              >
                Only this one
              </button>
              <button
                type="button"
                className="chip"
                aria-pressed={scope === 'series'}
                onClick={() => {
                  setScope('series')
                }}
              >
                All of the run
              </button>
            </div>
          </section>
        )}

        <Field
          id="appointment-title"
          label="Title"
          value={title}
          held={held('title')}
          source={appointment.origin.source}
          onValue={setTitle}
          onSave={() => {
            if (title.trim() !== '' && title !== appointment.title) {
              save({ title })
            }
          }}
          onRelease={() => {
            onRelease('title')
          }}
        />

        <div className="appointment-detail__times">
          <div className="appointment-detail__field">
            <label className="appointment-detail__label" htmlFor="appointment-start">
              Begins
            </label>
            {/* The browser's own field: a picker on a phone, a typed value on a Mac, and understood
                by a screen reader without anything being explained to it. */}
            <input
              id="appointment-start"
              type="datetime-local"
              className="appointment-detail__text"
              defaultValue={localValue(appointment.startsAt, appointment.zone)}
              onBlur={(event) => {
                const wanted = momentOfLocal(event.target.value, appointment.zone)
                if (wanted !== null && wanted !== appointment.startsAt) {
                  save({ startsAt: wanted })
                }
              }}
            />
          </div>
          <div className="appointment-detail__field">
            <label className="appointment-detail__label" htmlFor="appointment-end">
              Ends
            </label>
            <input
              id="appointment-end"
              type="datetime-local"
              className="appointment-detail__text"
              defaultValue={localValue(appointment.endsAt, appointment.zone)}
              onBlur={(event) => {
                const wanted = momentOfLocal(event.target.value, appointment.zone)
                if (wanted !== null && wanted !== appointment.endsAt) {
                  save({ endsAt: wanted })
                }
              }}
            />
          </div>
          {held('time') && (
            <HeldNote
              what="time"
              source={appointment.origin.source}
              onRelease={() => {
                onRelease('time')
              }}
            />
          )}
        </div>

        <Field
          id="appointment-room"
          label="Room"
          value={room}
          held={held('room')}
          source={appointment.origin.source}
          onValue={setRoom}
          onSave={() => {
            if (room !== appointment.room) {
              save({ room })
            }
          }}
          onRelease={() => {
            onRelease('room')
          }}
        />

        <Field
          id="appointment-teacher"
          label="Teacher"
          value={teacher}
          held={held('teacher')}
          source={appointment.origin.source}
          onValue={setTeacher}
          onSave={() => {
            if (teacher !== appointment.teacher) {
              save({ teacher })
            }
          }}
          onRelease={() => {
            onRelease('teacher')
          }}
        />

        <section className="appointment-detail__part">
          <SubjectPicker
            chosen={chosen}
            known={subjects.filter((subject) => subject.archivedAt === null)}
            single
            label="Subject"
            onFile={(subjectId) => {
              save({ subjectId })
            }}
            onUnfile={() => {
              save({ clearSubject: true })
            }}
            onCreate={onCreateSubject}
          />
          {appointment.subject === null && appointment.sourceSubject.key !== '' && (
            <p className="appointment-detail__aside">
              {appointment.origin.source} calls this{' '}
              <strong>
                {appointment.sourceSubject.name === ''
                  ? appointment.sourceSubject.key
                  : `${appointment.sourceSubject.name} (${appointment.sourceSubject.key})`}
              </strong>
              . Make a subject of it above and everything about it comes together.
            </p>
          )}
          {held('subject') && (
            <HeldNote
              what="subject"
              source={appointment.origin.source}
              onRelease={() => {
                onRelease('subject')
              }}
            />
          )}
        </section>

        <div className="appointment-detail__field">
          <label className="appointment-detail__label" htmlFor="appointment-note">
            Note
          </label>
          {/* Never comes from a source, and therefore never needs protecting from one. */}
          <RichText
            compact
            text={note}
            format={appointment.noteMarkup}
            label="Note at this appointment"
            purpose="appointment-note"
            initially="text"
            placeholder="Anything worth keeping about this hour."
            /* Saved as it is written, like every other text in MyLib. */
            onChange={(wanted) => {
              setNote(wanted)
              savingNote.typed(wanted)
            }}
            onFormat={async (noteMarkup) => {
              await save({ noteMarkup })
            }}
            onSubmit={savingNote.now}
          />
        </div>

        {/*
          Only what a source says about a *deviation* — a substitution, a moved hour. The text
          belonging to an entry is its note, and it is in the field above where it belongs.
        */}
        {appointment.remark !== '' && fromASource && (
          <p className="appointment-detail__aside">
            {appointment.origin.source} says: {appointment.remark}
          </p>
        )}

        <Repeats appointment={appointment} onRepeat={onRepeat} onStop={onStopRepeating} />

        <section className="appointment-detail__part">
          <h3 className="appointment-detail__part-title">Where this came from</h3>
          <p className="appointment-detail__aside">
            {originOf(appointment)}
            {!appointment.origin.canBeRecognised && appointment.origin.kind !== 'by-hand' && (
              <>
                {' '}
                It carries no id of its own, so reading that source again would add it a second
                time.
              </>
            )}
          </p>
          <div className="controls">
            <Button tone="quiet" className="control--danger" onClick={onRemove}>
              Delete this appointment
            </Button>
          </div>
        </section>
      </div>
    </article>
  )
}

function Field({
  id,
  label,
  value,
  held,
  source,
  onValue,
  onSave,
  onRelease,
}: {
  readonly id: string
  readonly label: string
  readonly value: string
  readonly held: boolean
  /** Which source this value is being kept against. Never empty where [held] is true. */
  readonly source: string
  readonly onValue: (value: string) => void
  readonly onSave: () => void
  readonly onRelease: () => void
}) {
  return (
    <div className="appointment-detail__field">
      <label className="appointment-detail__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="appointment-detail__text"
        value={value}
        onChange={(event) => {
          onValue(event.target.value)
        }}
        onBlur={onSave}
      />
      {held && <HeldNote what={label.toLowerCase()} source={source} onRelease={onRelease} />}
    </div>
  )
}

/**
 * What a corrected field says, and the way back out of it.
 *
 * Both halves matter. Without the sentence, a value that has stopped following the source would be
 * a silent surprise a term later; without the control, one hasty correction would freeze it for
 * ever. It only ever appears where there **is** a source — see `fromASource` above.
 */
function HeldNote({
  what,
  source,
  onRelease,
}: {
  readonly what: string
  readonly source: string
  readonly onRelease: () => void
}) {
  return (
    <p className="appointment-detail__held">
      <span>{`You changed the ${what}, so ${source} no longer overwrites it.`}</span>
      <Button tone="bare" onClick={onRelease}>
        {`Use ${source}’s value again`}
      </Button>
    </p>
  )
}

function originOf(appointment: Appointment): string {
  if (appointment.origin.kind === 'by-hand') {
    return 'Entered by hand.'
  }
  const source = appointment.origin.source === '' ? 'a source' : appointment.origin.source
  const seen =
    appointment.origin.seenAt === null
      ? ''
      : ` Last read ${readableMoment(appointment.origin.seenAt)}.`
  return `Taken over from ${source}.${seen}`
}

function readableMoment(iso: string): string {
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? iso : at.toLocaleString()
}
